import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { isProblemDetailTextOr } from 'src/common/ProblemDetail';
import { NotificationSeverity, showSnackbar } from 'src/features/notifications/notifications.slice';
import { useAppDispatch } from 'src/redux-app-hooks';

import { admiDataCollectionService } from '../admi-data-collection.service';
import {
    CollectedItem,
    GroupsForProduct,
    ProductRequirementGroup,
    RequirementField,
    RequirementFieldOption,
    RequirementFieldWithChildren,
} from '../data-collection.dto';
import { DealWithServices } from '../real-estate-asset-management.dto';
import {
    FieldValueMap,
    buildFieldTree,
    isFieldRequired,
    isFieldVisible,
    mergeCatalogWithProductGroups,
} from './data-collection.helpers';
import { activeProductIds } from './service-information.helpers';
import { ListEntry, ListFieldRenderer } from './ListFieldRenderer';
import { RequirementFieldInput } from './RequirementFieldInput';
import { SelectFieldInput } from './SelectFieldInput';

type DraftValue = string | number | boolean | string[] | ListEntry[] | null;

interface Props {
    realEstateAssetId: string;
    groupCode: string;
    reaServices: DealWithServices[];
    buildingOptions: RequirementFieldOption[];
    groups: ProductRequirementGroup[] | null;
    onGroupsLoaded?: (groups: ProductRequirementGroup[]) => void;
    /** Notifies the parent when the tab has unsaved draft edits. */
    onDirtyChange?: (dirty: boolean) => void;
    /**
     * Lifts each meter's building selection so the page can render numbered map
     * markers. `meterNumber` is 1-based (entry index + 1). Detected structurally:
     * the first LIST field whose sub-fields include a `buildings`-sourced select.
     */
    onBuildingMarkersChange?: (assignments: { meterNumber: number; buildingIds: string[] }[]) => void;
}

export function DataCollectionTab({
    realEstateAssetId,
    groupCode,
    reaServices,
    buildingOptions,
    groups: groupsProp,
    onGroupsLoaded,
    onDirtyChange,
    onBuildingMarkersChange,
}: Props) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [fetchedGroups, setFetchedGroups] = useState<ProductRequirementGroup[] | null>(null);
    const [collectedItems, setCollectedItems] = useState<CollectedItem[] | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);

    // Staged edits, keyed by requirement_code. Empty = nothing dirty.
    const [draftEdits, setDraftEdits] = useState<Map<string, DraftValue>>(() => new Map());
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    // Per-field error after a failed save, so the input can show its own hint.
    const [fieldErrors, setFieldErrors] = useState<Map<string, string>>(() => new Map());

    const productIds = useMemo(() => uniqueProductIds(reaServices), [reaServices]);

    useEffect(() => {
        if (groupsProp !== null) return;
        let cancelled = false;
        Promise.allSettled([
            admiDataCollectionService.getRequirementGroupsCatalog(),
            ...productIds.map((id) => admiDataCollectionService.getProductRequirementGroups(id)),
        ]).then(([catalogResult, ...productResults]) => {
            if (cancelled) return;
            const ok: GroupsForProduct[] = [];
            const failures: unknown[] = [];
            if (catalogResult.status === 'rejected') failures.push(catalogResult.reason);
            productResults.forEach((r, i) => {
                if (r.status === 'fulfilled') ok.push({ productId: productIds[i], groups: r.value });
                else failures.push(r.reason);
            });
            // Total failure → block the whole tab with an inline Alert (nothing
            // to render anyway). Partial failure → render what we got and
            // surface a snackbar so the user knows some tabs may be missing.
            const catalogOk = catalogResult.status === 'fulfilled';
            if (!catalogOk && ok.length === 0) {
                setLoadError(isProblemDetailTextOr(failures[0], t('Failed to load requirements')));
                return;
            }
            const merged = mergeCatalogWithProductGroups(catalogOk ? catalogResult.value : [], ok);
            setFetchedGroups(merged);
            onGroupsLoaded?.(merged);
            if (failures.length > 0) {
                const first = isProblemDetailTextOr(failures[0], t('Failed to load requirements'));
                const message =
                    failures.length === 1
                        ? first
                        : t('{{count}} products failed to load requirements: {{first}}', {
                              count: failures.length,
                              first,
                          });
                dispatch(showSnackbar({ message, severity: NotificationSeverity.Error }));
            }
        });
        return () => {
            cancelled = true;
        };
    }, [groupsProp, productIds, onGroupsLoaded, dispatch, t]);

    const groups = useMemo<ProductRequirementGroup[] | null>(
        () => groupsProp ?? fetchedGroups,
        [groupsProp, fetchedGroups],
    );
    const isLoading = groups === null || collectedItems === null;

    useEffect(() => {
        let cancelled = false;
        admiDataCollectionService
            .listCollectedItems(realEstateAssetId)
            .then((response) => {
                if (!cancelled) setCollectedItems(response.collectedItems);
            })
            .catch(() => {
                if (!cancelled) setCollectedItems([]);
            });
        return () => {
            cancelled = true;
        };
    }, [realEstateAssetId]);

    // Drop drafts whenever the REA or the active group changes; otherwise
    // a stale draft from one tab would leak into another.
    const lastResetKey = useRef<string>(`${realEstateAssetId}|${groupCode}`);
    useEffect(() => {
        const key = `${realEstateAssetId}|${groupCode}`;
        if (lastResetKey.current !== key) {
            lastResetKey.current = key;
            setDraftEdits(new Map());
            setFieldErrors(new Map());
            setSaveError(null);
        }
    }, [realEstateAssetId, groupCode]);

    const group = useMemo(() => groups?.find((g) => g.code === groupCode) ?? null, [groups, groupCode]);

    const fieldTree: RequirementFieldWithChildren[] = useMemo(
        () => (group ? buildFieldTree(group.requirementFields) : []),
        [group],
    );

    // Precompute the informational productCode per field once per (group, services).
    // `pickProductCodeForField` builds a Set and scans every service, so calling it
    // inline per field on each render would be O(fields × services) — and this
    // component re-renders on every keystroke (draftEdits churn).
    const productCodesMap = useMemo(() => {
        const map = new Map<string, string | null>();
        for (const field of group?.requirementFields ?? []) {
            map.set(field.code, pickProductCodeForField(field, reaServices));
        }
        return map;
    }, [group, reaServices]);

    const serviceOptions: RequirementFieldOption[] = useMemo(() => buildServiceOptions(reaServices), [reaServices]);

    const dynamicOptions: Record<string, RequirementFieldOption[]> = useMemo(
        () => ({ buildings: buildingOptions, services: serviceOptions }),
        [buildingOptions, serviceOptions],
    );

    const persistedValueMap: FieldValueMap = useMemo(() => {
        const latestByCode = new Map<string, CollectedItem>();
        if (collectedItems) {
            for (const item of collectedItems) {
                const prev = latestByCode.get(item.requirementCode);
                if (!prev || item.updatedAt > prev.updatedAt) {
                    latestByCode.set(item.requirementCode, item);
                }
            }
        }
        const map: FieldValueMap = {};
        for (const [code, item] of latestByCode) {
            const field = findFieldByCode(fieldTree, code);
            map[code] = decodeValue(item, field);
        }
        return map;
    }, [collectedItems, fieldTree]);

    // Effective value = draft (if dirty) ?? persisted. Drives input rendering
    // and `requiredWhen` evaluation as the user types.
    const effectiveValueMap: FieldValueMap = useMemo(() => {
        const map: FieldValueMap = { ...persistedValueMap };
        for (const [code, value] of draftEdits) {
            map[code] = value as unknown;
        }
        return map;
    }, [persistedValueMap, draftEdits]);

    // Lift each meter's building selection so the page can render numbered map
    // markers. Detected structurally (no hardcoded codes): the first LIST field
    // whose sub-fields include a `buildings`-sourced select. Draft-aware via
    // effectiveValueMap, so markers track edits live.
    // Detect the meters LIST + its `buildings` sub-field once per field tree, so the
    // structural lookup doesn't re-run on every keystroke (effectiveValueMap churns).
    const meterFieldCodes = useMemo(() => {
        const listField = fieldTree.find(
            (f) => f.fieldType === 'list' && f.subFields.some((sf) => sf.optionSource === 'buildings'),
        );
        const buildingsSub = listField?.subFields.find((sf) => sf.optionSource === 'buildings');
        return listField && buildingsSub ? { listCode: listField.code, buildingsSubCode: buildingsSub.code } : null;
    }, [fieldTree]);

    // Recompute on value changes, but keep a stable array reference when the actual
    // meter→building mapping is unchanged — otherwise typing in any field would re-emit
    // and re-render the parent map on every keystroke.
    const assignmentsRef = useRef<{ meterNumber: number; buildingIds: string[] }[]>([]);
    const buildingMarkerAssignments = useMemo<{ meterNumber: number; buildingIds: string[] }[]>(() => {
        let next: { meterNumber: number; buildingIds: string[] }[] = [];
        if (meterFieldCodes) {
            const rawEntries = effectiveValueMap[meterFieldCodes.listCode];
            const entries = Array.isArray(rawEntries) ? (rawEntries as ListEntry[]) : [];
            next = entries
                .map((entry, i) => {
                    const raw = entry[meterFieldCodes.buildingsSubCode];
                    const buildingIds = Array.isArray(raw)
                        ? (raw as string[])
                        : typeof raw === 'string' && raw
                          ? [raw]
                          : [];
                    return { meterNumber: i + 1, buildingIds };
                })
                .filter((a) => a.buildingIds.length > 0);
        }
        // Referential stability: reuse the previous array when nothing meaningful changed.
        if (sameMeterAssignments(next, assignmentsRef.current)) return assignmentsRef.current;
        assignmentsRef.current = next;
        return next;
    }, [meterFieldCodes, effectiveValueMap]);

    // Emit the current assignments; the cleanup clears them. On unmount (navigating to
    // a static tab) no markers are left behind. On a re-emit, React batches the cleanup's
    // `[]` with the new value in the same flush, so the map never renders an empty frame.
    useEffect(() => {
        onBuildingMarkersChange?.(buildingMarkerAssignments);
        return () => onBuildingMarkersChange?.([]);
    }, [buildingMarkerAssignments, onBuildingMarkersChange]);

    const isDirty = draftEdits.size > 0;

    useEffect(() => {
        onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);

    const handleFieldChange = (code: string, next: DraftValue) => {
        setDraftEdits((prev) => {
            const m = new Map(prev);
            m.set(code, next);
            return m;
        });
        setFieldErrors((prev) => {
            if (!prev.has(code)) return prev;
            const m = new Map(prev);
            m.delete(code);
            return m;
        });
    };

    const handleCancel = () => {
        setDraftEdits(new Map());
        setFieldErrors(new Map());
        setSaveError(null);
    };

    const handleSave = async () => {
        if (!group || draftEdits.size === 0) return;
        setIsSaving(true);
        setSaveError(null);
        const remaining = new Map(draftEdits);
        const errors = new Map<string, string>();
        // Answers are stored once at the REA grain — a single upsert per field,
        // never fanned out per service. `productCode` is informational (which
        // product this answer was collected under); the backend owns the
        // storage-routing decision via the field's entity type. Upserts run in
        // parallel; the synchronous map/array mutations between awaits are
        // race-free on JS's single thread.
        const savedItems: CollectedItem[] = [];
        try {
            await Promise.all(
                Array.from(draftEdits).map(async ([code, value]) => {
                    const field = findFieldByCode(fieldTree, code);
                    if (!field) {
                        remaining.delete(code);
                        return;
                    }
                    const productCode = productCodesMap.get(field.code) ?? null;
                    const valueText = encodeValueText(field, value);
                    try {
                        const item = await admiDataCollectionService.upsertCollectedItem(realEstateAssetId, {
                            requirementCode: field.code,
                            productCode,
                            valueText,
                        });
                        savedItems.push(item);
                        remaining.delete(code);
                    } catch (e) {
                        errors.set(code, e instanceof Error ? e.message : t('Failed to save'));
                    }
                }),
            );
            if (savedItems.length > 0) {
                setCollectedItems((prev) => {
                    let next = prev ?? [];
                    for (const item of savedItems) next = upsertItem(next, item);
                    return next;
                });
            }
        } finally {
            setDraftEdits(remaining);
            setFieldErrors(errors);
            if (errors.size > 0) {
                setSaveError(t('Some fields could not be saved. See the highlighted inputs.'));
            }
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={20} />
            </Box>
        );
    }

    if (loadError) {
        return <Alert severity="error">{loadError}</Alert>;
    }

    if (!group) {
        return <Alert severity="info">{t('No fields are defined for this section.')}</Alert>;
    }

    const visibleFields = fieldTree.filter((f) => isFieldVisible(f, effectiveValueMap));

    // SERVICE-entity answers are stored per service (GEN-591) — without an
    // ACTIVE connected service of an owning product there is nothing to write
    // to and the backend rejects the save, so those fields are disabled up
    // front. REA-grain fields stay always-editable (GEN-653).
    const activeProducts = activeProductIds(reaServices);

    return (
        <Stack spacing={2} sx={{ mt: 1 }}>
            {saveError && <Alert severity="error">{saveError}</Alert>}

            {visibleFields.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    {t('No fields to fill in for this section.')}
                </Typography>
            )}
            {visibleFields.map((field) => {
                // All catalog fields are editable, even without a linked service
                // (GEN-653) — the answer is then stored without product attribution.
                // Exception: SERVICE-entity fields need an active owning service.
                const disabled =
                    field.entityType === 'service' && !field.productIds?.some((id) => activeProducts.has(id));
                const disabledReason = disabled ? t('Connect a service to collect its delivery information.') : null;
                const required = isFieldRequired(field, effectiveValueMap);
                const errorMessage = fieldErrors.get(field.code) ?? null;

                if (field.fieldType === 'list') {
                    const rawValue = effectiveValueMap[field.code];
                    const value = Array.isArray(rawValue) ? (rawValue as ListEntry[]) : [];
                    return (
                        <ListFieldRenderer
                            key={field.id}
                            field={field}
                            entries={value}
                            dynamicOptions={dynamicOptions}
                            parentValues={effectiveValueMap}
                            disabled={disabled}
                            disabledReason={disabledReason}
                            onChange={(next) => handleFieldChange(field.code, next)}
                        />
                    );
                }

                if (field.fieldType === 'select') {
                    const options =
                        field.optionSource === 'static'
                            ? (field.options ?? [])
                            : (dynamicOptions[field.optionSource ?? ''] ?? []);
                    const raw = effectiveValueMap[field.code];
                    const selectValue = field.multiple
                        ? Array.isArray(raw)
                            ? (raw as string[])
                            : []
                        : typeof raw === 'string'
                          ? raw
                          : null;
                    return (
                        <SelectFieldInput
                            key={field.id}
                            field={field}
                            value={selectValue}
                            options={options}
                            required={required}
                            disabled={disabled}
                            disabledReason={disabledReason}
                            errorMessage={errorMessage}
                            onChange={(next) => handleFieldChange(field.code, next)}
                        />
                    );
                }

                return (
                    <RequirementFieldInput
                        key={field.id}
                        field={field}
                        value={(effectiveValueMap[field.code] as string | number | boolean | null | undefined) ?? null}
                        required={required}
                        disabled={disabled}
                        disabledReason={disabledReason}
                        errorMessage={errorMessage}
                        onChange={(next) => handleFieldChange(field.code, next)}
                    />
                );
            })}

            <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end', pt: 1 }}>
                <Button onClick={handleCancel} disabled={!isDirty || isSaving} size="small">
                    {t('Cancel')}
                </Button>
                <Button
                    onClick={() => void handleSave()}
                    disabled={!isDirty || isSaving}
                    variant="contained"
                    size="small"
                    startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : undefined}
                >
                    {t('Save')}
                </Button>
            </Stack>
        </Stack>
    );
}

/** Structural equality for the lifted meter→building assignments (preserves array identity). */
function sameMeterAssignments(
    a: { meterNumber: number; buildingIds: string[] }[],
    b: { meterNumber: number; buildingIds: string[] }[],
): boolean {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        if (a[i].meterNumber !== b[i].meterNumber) return false;
        const ab = a[i].buildingIds;
        const bb = b[i].buildingIds;
        if (ab.length !== bb.length) return false;
        for (let j = 0; j < ab.length; j++) {
            if (ab[j] !== bb[j]) return false;
        }
    }
    return true;
}

function uniqueProductIds(reaServices: DealWithServices[]): string[] {
    const set = new Set<string>();
    for (const deal of reaServices) {
        for (const svc of deal.services) {
            if (svc.productId) set.add(svc.productId);
        }
    }
    return [...set];
}

function buildServiceOptions(reaServices: DealWithServices[]): RequirementFieldOption[] {
    const out: RequirementFieldOption[] = [];
    for (const deal of reaServices) {
        for (const service of deal.services) {
            out.push({ value: service.id, label: `${service.name} (${deal.name})` });
        }
    }
    return out;
}

function findFieldByCode(tree: RequirementFieldWithChildren[], code: string): RequirementFieldWithChildren | null {
    for (const f of tree) {
        if (f.code === code) return f;
        for (const child of f.subFields) {
            if (child.code === code) return child;
        }
    }
    return null;
}

function decodeValue(item: CollectedItem, field: RequirementField | null): unknown {
    if (field?.fieldType === 'list') {
        if (!item.valueText) return [];
        try {
            const parsed = JSON.parse(item.valueText);
            return Array.isArray(parsed) ? (parsed as ListEntry[]) : [];
        } catch {
            return [];
        }
    }
    if (field?.fieldType === 'select' && field.multiple) {
        if (!item.valueText) return [];
        try {
            const parsed = JSON.parse(item.valueText);
            return Array.isArray(parsed) ? (parsed as string[]) : [];
        } catch {
            return [];
        }
    }
    if (field?.fieldType === 'select') {
        return item.valueText;
    }
    if (!field) return item.valueText;
    if (item.valueText === null || item.valueText === undefined) return null;
    if (field.fieldType === 'number') {
        const n = Number(item.valueText);
        return Number.isFinite(n) ? n : null;
    }
    if (field.fieldType === 'boolean') return item.valueText === 'true';
    return item.valueText;
}

function encodeValueText(field: RequirementField, value: DraftValue): string | null {
    if (field.fieldType === 'list') {
        return JSON.stringify(Array.isArray(value) ? value : []);
    }
    if (field.fieldType === 'select' && field.multiple) {
        return JSON.stringify(Array.isArray(value) ? value : []);
    }
    if (field.fieldType === 'select') {
        // A cleared selection must save as BLANK, not null: the REA-grain
        // store treats a null valueText as "leave unchanged" (partial update),
        // while blank is the system-wide "unanswered" (CollectedItem.hasValue).
        return value == null ? '' : String(value);
    }
    if (value === null || value === undefined) return null;
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
}

/**
 * Pick the `productCode` (SKU) to record on a REA-grain answer. Answers are no
 * longer written per service, but the row still carries an informational product
 * code: prefer a service whose product owns the field (`field.productIds`,
 * populated by the per-product fetch), else fall back to any linked service's
 * SKU. Returns `null` when no linked service has a SKU — the answer is then
 * stored without product attribution (GEN-653); the field stays editable.
 */
function pickProductCodeForField(field: RequirementField, reaServices: DealWithServices[]): string | null {
    const owning = new Set<string>(field.productIds ?? []);
    let fallback: string | null = null;
    for (const deal of reaServices) {
        for (const service of deal.services) {
            if (!service.sku) continue;
            if (service.productId && owning.has(service.productId)) return service.sku;
            fallback ??= service.sku;
        }
    }
    return fallback;
}

function upsertItem(items: CollectedItem[], next: CollectedItem): CollectedItem[] {
    const idx = items.findIndex((i) => i.code === next.code);
    if (idx === -1) return [...items, next];
    const copy = [...items];
    copy[idx] = next;
    return copy;
}
